import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useUserRoles, AppRole, hasAnyRole } from '@/hooks/useUserRoles';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Shield, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface RoleProtectedRouteProps {
  children: ReactNode;
  requiredRoles: AppRole[];
  requireAll?: boolean; // true = يجب توفر جميع الأدوار, false = يكفي دور واحد
  fallbackPath?: string;
}

export function RoleProtectedRoute({ 
  children, 
  requiredRoles, 
  requireAll = false,
  fallbackPath = '/dashboard' 
}: RoleProtectedRouteProps) {
  const { user } = useAuth();
  const { data: userRoles, isLoading } = useUserRoles(user?.id);
  
  // Admin emails bypass all checks
  const adminEmails = ['klidmorre@gmail.com', 'eng.khalid.work@gmail.com'];
  const isSystemAdmin = user?.employeeData?.email && adminEmails.includes(user.employeeData.email);
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // System admins have access to everything
  if (isSystemAdmin) {
    return <>{children}</>;
  }

  // Check role access
  const hasAccess = requireAll 
    ? requiredRoles.every(role => userRoles?.some(r => r.role === role))
    : hasAnyRole(userRoles, requiredRoles);

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertTriangle className="h-5 w-5" />
          <AlertTitle className="text-lg font-bold mb-2">
            ليس لديك صلاحية للوصول
          </AlertTitle>
          <AlertDescription className="space-y-3">
            <p className="text-sm">
              هذه الصفحة محمية وتتطلب أحد الأدوار التالية:
            </p>
            <div className="flex flex-wrap gap-2">
              {requiredRoles.map(role => (
                <span 
                  key={role} 
                  className="px-2 py-1 bg-destructive/10 text-destructive rounded text-xs font-medium"
                >
                  {role}
                </span>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => window.history.back()}
              >
                رجوع
              </Button>
              <Button 
                size="sm"
                onClick={() => window.location.href = fallbackPath}
              >
                الذهاب للرئيسية
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
}
